// src/services/chatProxyService.js
import api, { isAuthenticated } from "../utils/api";
import { API_CONFIG } from "../utils/apiConfig";

/**
 * Real-time chat proxy.
 *
 * Chat only becomes "real" (persisted to the mobile backend's `flows` /
 * `flow_messages` tables and delivered to paymeo-webapp sockets) AFTER a
 * buyer accepts the seller's offer in paymeo-webapp, which creates the
 * flow + backfills the context/offer messages. Before that, the frontend
 * keeps messages as local drafts.
 *
 * Each call here goes through the invoice_generator proxy, which exchanges
 * the logged-in user's session for a mobile-backend session token and
 * forwards to MOBILE_ENDPOINT (the same external-service pattern used by
 * the offers/asks flows), so replies reach every connected webapp client in
 * the flow room at real time.
 */

const normalizeError = (err) =>
    err.response?.data?.message || err.response?.data?.error || err.message || 'Request failed';

/**
 * Fetch the real persisted messages for an ask (only non-empty once the
 * buyer has accepted an offer and a flow exists).
 */
export const fetchAskMessages = async (askId) => {
    if (!isAuthenticated()) {
        return { success: false, error: 'Authentication required. Please log in.', status: 401, data: null };
    }
    if (!askId) {
        return { success: false, error: 'Ask ID is required', status: 400, data: null };
    }
    try {
        const response = await api.get(`${API_CONFIG.CHAT.GET_ASK_MESSAGES}/${askId}/messages`);
        return {
            success: true,
            data: response.data?.data || response.data,
            status: response.status
        };
    } catch (error) {
        return {
            success: false,
            error: normalizeError(error),
            status: error.response?.status || 500,
            data: null
        };
    }
};

/**
 * Send a real, persisted message for an ask. Requires an accepted flow to
 * exist in the mobile backend; otherwise it returns the backend error
 * (typically 403/404) and the caller should fall back to a local draft.
 */
export const sendAskMessage = async (askId, { content, image_urls, video_urls }) => {
    if (!isAuthenticated()) {
        return { success: false, error: 'Authentication required. Please log in.', status: 401, data: null };
    }
    if (!askId) {
        return { success: false, error: 'Ask ID is required', status: 400, data: null };
    }
    if (!content && (!image_urls || image_urls.length === 0) && (!video_urls || video_urls.length === 0)) {
        return { success: false, error: 'Message content or media is required', status: 400, data: null };
    }
    try {
        const body = { content };
        if (image_urls) body.image_urls = image_urls;
        if (video_urls) body.video_urls = video_urls;

        const response = await api.post(`${API_CONFIG.CHAT.SEND_ASK_MESSAGE}/${askId}/messages/send`, body);
        return {
            success: true,
            data: response.data?.data || response.data,
            status: response.status
        };
    } catch (error) {
        return {
            success: false,
            error: normalizeError(error),
            status: error.response?.status || 500,
            data: null
        };
    }
};

/** Fetch all accepted conversations (flows) for the logged-in seller. */
export const fetchFlows = async () => {
    if (!isAuthenticated()) {
        return { success: false, error: 'Authentication required. Please log in.', status: 401, data: null };
    }
    try {
        const response = await api.get(API_CONFIG.CHAT.GET_FLOWS);
        return {
            success: true,
            data: response.data?.data || response.data,
            status: response.status
        };
    } catch (error) {
        return {
            success: false,
            error: normalizeError(error),
            status: error.response?.status || 500,
            data: null
        };
    }
};

/** Fetch a single flow with its full message list and participants. */
export const fetchFlowDetail = async (flowId) => {
    if (!isAuthenticated()) {
        return { success: false, error: 'Authentication required. Please log in.', status: 401, data: null };
    }
    if (!flowId) {
        return { success: false, error: 'Flow ID is required', status: 400, data: null };
    }
    try {
        const response = await api.get(`${API_CONFIG.CHAT.GET_FLOW}/${flowId}`);
        return {
            success: true,
            data: response.data?.data || response.data,
            status: response.status
        };
    } catch (error) {
        return {
            success: false,
            error: normalizeError(error),
            status: error.response?.status || 500,
            data: null
        };
    }
};

/** Mark a flow as read for the logged-in seller. */
export const markFlowRead = async (flowId) => {
    if (!isAuthenticated()) {
        return { success: false, error: 'Authentication required. Please log in.', status: 401, data: null };
    }
    if (!flowId) {
        return { success: false, error: 'Flow ID is required', status: 400, data: null };
    }
    try {
        const response = await api.post(`${API_CONFIG.CHAT.MARK_FLOW_READ}/${flowId}/read`);
        return { success: true, data: response.data, status: response.status };
    } catch (error) {
        return { success: false, error: normalizeError(error), status: error.response?.status || 500, data: null };
    }
};